import { RegisterCardResultCode, vendorSchema, yesNoSchema } from '@server/schemas'
import { z } from 'zod'

export const paymentMethodTypeSchema = z.enum([
  'CARD',
  'KAKAO_PAY',
  'NAVER_PAY',
  'REGISTERED_CARD',
  'REGISTERED_KAKAO_PAY',
  'REGISTERED_NAVER_PAY',
])

export type PaymentMethodType = z.infer<typeof paymentMethodTypeSchema>

export type PaymentMethod<Provider extends PaymentMethodType = PaymentMethodType> = {
  provider: Provider
  name: string
  icon: React.ReactNode
}

type RegisterCardFailResultCode = Exclude<RegisterCardResultCode, 'OK'>
type RegisterCardSuccessResultCode = Extract<RegisterCardResultCode, 'OK'>

export const isRegisterCardSuccessResultCode = (code: string): code is RegisterCardSuccessResultCode => {
  return Object.values(REGISTER_CARD_SUCCESS_RESULT_CODES).includes(code as RegisterCardSuccessResultCode)
}

export const isRegisterCardFailResultCode = (code: unknown): code is RegisterCardFailResultCode => {
  return Object.values(REGISTER_CARD_FAIL_RESULT_CODES).includes(code as RegisterCardFailResultCode)
}

export const REGISTER_CARD_SUCCESS_RESULT_CODES: { [key in RegisterCardSuccessResultCode]: key } = {
  OK: 'OK',
}

export const REGISTER_CARD_FAIL_RESULT_CODES: { [key in Exclude<RegisterCardResultCode, 'OK'>]: key } = {
  INVALID_CARD_TYPE: 'INVALID_CARD_TYPE',
  INVALID_CARD_EXPIRATION: 'INVALID_CARD_EXPIRATION',
  REJECT_CARD_COMPANY: 'REJECT_CARD_COMPANY',
  INVALID_CARD_IDENTITY: 'INVALID_CARD_IDENTITY',
  EXCEED_MAX_AUTH_COUNT: 'EXCEED_MAX_AUTH_COUNT',
  INVALID_CARD: 'INVALID_CARD',
}

export const rawGetPaymentMethodSchema = z.object({
  card_bin4: z.string().nullable(),
  card_name: z.string(),
  debit_yn: yesNoSchema,
  id: z.string(),
  selected: z.boolean(),
  vendor: vendorSchema.nullable(),
})

export const rawGetPaymentMethodResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.null(),
  data: z.array(rawGetPaymentMethodSchema),
})

export type RawGetPaymentMethodResponseSchema = z.infer<typeof rawGetPaymentMethodResponseSchema>
export type RawGetPaymentMethod = z.infer<typeof rawGetPaymentMethodSchema>
